import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import ZoneDefinitionDetails from 'Services/ProductManagement/Features/ZoneDefinition/Components/ZoneDefinitionDetails'
import ZoneDefinitionList from 'Services/ProductManagement/Features/ZoneDefinition/Components/ZoneDefinitionList'
import { useZoneDefinitionsWithParams } from 'Services/ProductManagement/Features/ZoneDefinition/Hooks'
import {
  useDeleteSingleZoneDefinitionMutation,
  useGetSingleZoneDefinitionQuery
} from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import useTypedSelector from 'Store'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function ZoneDefinitionListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/zone-definitions`

  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { user } = useTypedSelector(state => state.user)

  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`Zone definitions`,
      link: baseUrl
    }
  ]

  const {
    zoneDefinitions,
    isZoneDefinitionsLoading,
    params,
    setParams,
    total_results,
    selectedZoneDefinitionId,
    setSelectedZoneDefinitionId
  } = useZoneDefinitionsWithParams()

  const { data: singleZoneDefinition, isLoading: isSingleZoneDefinitionLoading } =
    useGetSingleZoneDefinitionQuery(selectedZoneDefinitionId ?? '', {
      skip: !selectedZoneDefinitionId
    })

  const [deleteSingleZoneDefinition, { isLoading: isDeleteLoading }] =
    useDeleteSingleZoneDefinitionMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedZoneDefinitionId)

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleDrawerClose = () => {
    setSelectedZoneDefinitionId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedZoneDefinitionId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedZoneDefinitionId) return
    deleteSingleZoneDefinition(selectedZoneDefinitionId)
      .unwrap()
      .then(() => {
        setSelectedZoneDefinitionId('')
        setIsDrawerOpen(false)
        SnackbarUtils.success(t`Zone definition successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the zone definition.`)
      })
  }

  const isLoading = isZoneDefinitionsLoading || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Zone definitions`}</PageTitle>
      </Box>
      <ListActions>
        {isSuperuser(user) && (
          <CustomButton
            onClick={() => navigate(`${baseUrl}/create`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new zone definition`}
          </CustomButton>
        )}
      </ListActions>
      <Box pt={1}>
        <ZoneDefinitionList
          rows={zoneDefinitions}
          isLoading={isLoading}
          params={params}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          count={total_results}
          page={
            params.start_index ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0
          }
          onRowClick={handleRowClick}
          selectedZoneDefinitionId={selectedZoneDefinitionId}
        />
      </Box>
      <DrawerRight
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        topBar={
          <Box>
            {isSuperuser(user) && (
              <>
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the zone definition?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => navigate(`${baseUrl}/${selectedZoneDefinitionId}/edit`)}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </>
            )}
          </Box>
        }
      >
        <WithLoader loading={isSingleZoneDefinitionLoading || isDeleteLoading}>
          {singleZoneDefinition && (
            <TabContext value={'details'}>
              <TabList aria-label={t`Details`}>
                <Tab label={t`Details`} value={'details'} />
              </TabList>
              <Divider />
              <TabPanel sx={{ p: 0 }} value={'details'}>
                <ZoneDefinitionDetails zoneDefinition={singleZoneDefinition} />
              </TabPanel>
            </TabContext>
          )}
        </WithLoader>
      </DrawerRight>
    </Box>
  )
}
